import {useLazyQuery} from "@apollo/client";
import isNumber from "lodash/isNumber";
import {useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import {GQL_MARKDOWN_RENDER} from "@/lib/queries/common.ts";
import {MarkdownResult, ServerErrors} from "@/lib/types/common.ts";
import {IncidentState} from "@/lib/types/statuspages.ts";
import {UpdateFieldEdit} from "@/pages/incidents/components/incident-edit/components/updates/UpdateFieldEdit.tsx";
import {UpdateFieldPreview} from "@/pages/incidents/components/incident-edit/components/updates/UpdateFieldPreview.tsx";

interface UpdateFieldProps {
  name: string;
  descriptionHTML?: string;
  updatedAt?: string;
  updateRequired: boolean;
  onDelete: () => void;
  id?: number;
  onEditStateChange: (isEditing: boolean) => void;
  serverErrors?: ServerErrors;
  variant: "standalone" | "history";
}

export const UpdateField = ({
  name,
  updatedAt,
  descriptionHTML,
  onDelete,
  updateRequired,
  id,
  onEditStateChange,
  variant,
}: UpdateFieldProps) => {
  const {watch, setValue, trigger} = useFormContext();
  const [descriptionRendered, setDescriptionHTML] = useState<string>(descriptionHTML || "");
  const confirmed = watch(`${name}.confirmed`);
  const [isEditing, setIsEditing] = useState<boolean>(!isNumber(id) && !confirmed);
  const [renderMarkdown] = useLazyQuery<MarkdownResult>(GQL_MARKDOWN_RENDER);
  const endsAt = watch(`ends_at`);

  useEffect(() => {
    if (!isNumber(id)) {
      setIsEditing(!confirmed);
    }
  }, [confirmed, id]);

  useEffect(() => {
    // this is caused by insert new update into pos 1, every component has to be re-rendered
    setDescriptionHTML(descriptionHTML || "");
  }, [descriptionHTML]);

  useEffect(() => {
    onEditStateChange(isEditing);
  }, [isEditing, onEditStateChange]);

  const previewMarkdown = async (description: string, state: IncidentState) => {
    const result = await renderMarkdown({
      variables: {
        markdown: description,
      },
    });
    setDescriptionHTML(result?.data?.renderMarkdown.data || "");
    setValue(`${name}.confirmed`, true);
    // Trigger validation to clear the confirmed error
    await trigger(`${name}.confirmed`);
    setIsEditing(false);
    if (state === IncidentState.RESOLVED && !endsAt) {
      setValue("ends_at", new Date().toISOString());
    }
  };

  if (isEditing) {
    return <UpdateFieldEdit previewMarkdown={previewMarkdown} name={name} />;
  }
  return (
    <UpdateFieldPreview
      name={name}
      descriptionHTML={descriptionRendered}
      updatedAt={updatedAt}
      variant={variant}
      updateRequired={updateRequired}
      onEditClick={() => {
        setValue(`${name}.confirmed`, false);
        setIsEditing(true);
      }}
      onDeleteClick={onDelete}
    />
  );
};
